import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { FieldArray, FieldArrayRenderProps, useField } from 'formik';
import { Option } from 'lib/types';
import { TextFormField } from './TextFormField';
import { SelectFormField } from './SelectFormField';
import { NumberFormField } from './NumberFormField';
import { AutocompleteFormField } from './AutocompleteFormField';

type InputVariant = 'text' | 'number' | 'singleSelect' | 'autocomplete';

interface IInputConfig {
    variant: InputVariant;
    options?: Option[];
    placeholder?: string;
}

interface IDictionaryFormFieldProps {
    name: string;
    keyConfig: IInputConfig;
    valueConfig: IInputConfig;
    addButtonLabel?: string;
}

export interface IDictionaryEntry {
    key: string;
    value: string;
}

export const dictToEntries = (record?: Record<string, string>): IDictionaryEntry[] =>
    record ? Object.entries(record).map(([key, value]) => ({ key, value })) : [];

export const entriesToDict = (entries: IDictionaryEntry[] = []): Record<string, string> => {
    if (!Object.keys(entries).length) {
        return null;
    }

    return entries.reduce<Record<string, string>>((acc, { key, value }) => {
        if (key && value) {
            acc[key] = value;
        }
        return acc;
    }, {});
};

const renderInput = (fieldName: string, config: IInputConfig, options?: Option[]) => {
    switch (config.variant) {
        case 'number':
            return <NumberFormField name={fieldName} placeholder={config.placeholder} size="small" />;
        case 'singleSelect':
            return <SelectFormField name={fieldName} options={options ?? []} size="small" />;
        case 'autocomplete':
            return (
                <AutocompleteFormField
                    name={fieldName}
                    options={options ?? []}
                    placeholder={config.placeholder}
                    size="small"
                />
            );
        case 'text':
        default:
            return <TextFormField name={fieldName} placeholder={config.placeholder} size="small" />;
    }
};

interface IEntryRowProps {
    name: string;
    index: number;
    keyConfig: IInputConfig;
    valueConfig: IInputConfig;
    keyOptions?: Option[];
    onRemove: (index: number) => void;
}

const EntryRow: React.FC<IEntryRowProps> = ({
    name,
    index,
    keyConfig,
    valueConfig,
    keyOptions,
    onRemove
}) => {
    const handleRemove = React.useCallback(() => onRemove(index), [index, onRemove]);
    return (
        <Box display="flex" gap={1} alignItems="center">
            <Box width={180}>{renderInput(`${name}.${index}.key`, keyConfig, keyOptions)}</Box>
            <Box flex={1} minWidth={0}>
                {renderInput(`${name}.${index}.value`, valueConfig)}
            </Box>
            <IconButton onClick={handleRemove} size="small" aria-label="Remove entry">
                <Delete fontSize="small" />
            </IconButton>
        </Box>
    );
};

interface IFieldArrayContentProps {
    entries: IDictionaryEntry[];
    push: FieldArrayRenderProps['push'];
    remove: FieldArrayRenderProps['remove'];
    name: string;
    keyConfig: IInputConfig;
    valueConfig: IInputConfig;
    addButtonLabel: string;
}

const FieldArrayContent: React.FC<IFieldArrayContentProps> = ({
    entries,
    push,
    remove,
    name,
    keyConfig,
    valueConfig,
    addButtonLabel
}) => {
    const handleAdd = React.useCallback(() => push({ key: '', value: '' }), [push]);

    const hasAvailable = !keyConfig.options || entries.length < keyConfig.options.length;

    const filteredKeyOptionsByIndex = React.useMemo(
        () =>
            entries.map((_, index) => {
                if (!keyConfig.options) {
                    return undefined;
                }
                const otherKeys = entries
                    .filter((__, i) => i !== index)
                    .map(e => String(e.key))
                    .filter(Boolean);
                return keyConfig.options.filter(o => !otherKeys.includes(String(o.value)));
            }),
        [entries, keyConfig.options]
    );

    return (
        <Box display="flex" flexDirection="column" gap={1} sx={{ width: '100%' }}>
            {entries.map((_, index) => (
                <EntryRow
                    key={index}
                    name={name}
                    index={index}
                    keyConfig={keyConfig}
                    valueConfig={valueConfig}
                    keyOptions={filteredKeyOptionsByIndex[index]}
                    onRemove={remove}
                />
            ))}
            {hasAvailable && (
                <Box>
                    <Button startIcon={<Add />} onClick={handleAdd} size="small" variant="outlined">
                        {addButtonLabel}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export const DictionaryFormField: React.FC<IDictionaryFormFieldProps> = ({
    name,
    keyConfig,
    valueConfig,
    addButtonLabel = 'Add'
}) => {
    const [field] = useField<IDictionaryEntry[]>(name);
    const entries = field.value ?? [];

    return (
        <FieldArray name={name}>
            {({ push, remove }: FieldArrayRenderProps) => (
                <FieldArrayContent
                    entries={entries}
                    push={push}
                    remove={remove}
                    name={name}
                    keyConfig={keyConfig}
                    valueConfig={valueConfig}
                    addButtonLabel={addButtonLabel}
                />
            )}
        </FieldArray>
    );
};
